import * as React from "react";
import { type Table } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "@bcc/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import type { DataTableRow } from "./data-table.types";
import {
  setPaginationTestId,
  setPageSizeSelectTestId,
  setFirstPageButtonTestId,
  setPrevPageButtonTestId,
  setNextPageButtonTestId,
  setLastPageButtonTestId,
  setPageIndicatorTestId,
  setRowCountTestId,
} from "./data-table.utils";

interface DataTablePaginationProps<T extends object> {
  table: Table<DataTableRow<T>>;
  pageSizeOptions?: number[];
  totalRows?: number;
  showSelectedCount?: boolean;
}

/**
 * DataTablePagination
 *
 * Renders pagination controls with page size selector,
 * page navigation, and row count display.
 */
export function DataTablePagination<T extends object>({
  table,
  pageSizeOptions = [10, 20, 30, 50, 100],
  totalRows,
  showSelectedCount = true,
}: Readonly<DataTablePaginationProps<T>>) {
  const { t } = useTranslation();
  const selectedCount = table.getFilteredSelectedRowModel().rows.length;
  const totalCount = totalRows ?? table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;
  const pageSize = table.getState().pagination.pageSize;

  const texts = React.useMemo(
    () => ({
      rowsPerPage: t("dataTable.pagination.rowsPerPage"),
      pageOf: t("dataTable.pagination.pageOf", {
        current: currentPage,
        total: pageCount || 1,
      }),
      rowsSelected: t("dataTable.pagination.rowsSelected", {
        selected: selectedCount,
        total: totalCount,
      }),
      rowsTotal: t("dataTable.pagination.rowsTotal", { count: totalCount }),
      firstPage: t("dataTable.pagination.firstPage"),
      previousPage: t("dataTable.pagination.previousPage"),
      nextPage: t("dataTable.pagination.nextPage"),
      lastPage: t("dataTable.pagination.lastPage"),
    }),
    [t, currentPage, pageCount, selectedCount, totalCount]
  );

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not in an input
      if (document.activeElement?.tagName === "INPUT" || document.activeElement?.tagName === "TEXTAREA") {
        return;
      }

      if (e.key === "ArrowLeft" && table.getCanPreviousPage()) {
        e.preventDefault();
        table.previousPage();
      } else if (e.key === "ArrowRight" && table.getCanNextPage()) {
        e.preventDefault();
        table.nextPage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [table]);

  return (
    <div data-testid={setPaginationTestId()} className="flex items-center justify-between px-4 py-3 border-t border-border bg-card">
      {/* Left side - Selected count */}
      <div data-testid={setRowCountTestId()} className="flex-1 text-sm text-muted-foreground">
        {showSelectedCount && selectedCount > 0 ? <span>{texts.rowsSelected}</span> : <span>{texts.rowsTotal}</span>}
      </div>

      {/* Right side - Pagination controls */}
      <div className="flex items-center space-x-6 lg:space-x-8">
        {/* Page size selector */}
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">{texts.rowsPerPage}</p>
          <Select value={String(pageSize)} onValueChange={(value) => table.setPageSize(Number(value))}>
            <SelectTrigger data-testid={setPageSizeSelectTestId()} className="h-8 w-[80px]">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Page indicator */}
        <div data-testid={setPageIndicatorTestId()} className="flex w-[100px] items-center justify-center text-sm font-medium">
          {texts.pageOf}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center space-x-2">
          <Button
            data-testid={setFirstPageButtonTestId()}
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            aria-label={texts.firstPage}
          >
            <ChevronsLeft className="size-4" />
          </Button>
          <Button
            data-testid={setPrevPageButtonTestId()}
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label={texts.previousPage}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            data-testid={setNextPageButtonTestId()}
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label={texts.nextPage}
          >
            <ChevronRight className="size-4" />
          </Button>
          <Button
            data-testid={setLastPageButtonTestId()}
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={!table.getCanNextPage()}
            aria-label={texts.lastPage}
          >
            <ChevronsRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
